/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { CSSProperties, FunctionComponent, JSX } from "react";

import { CarouselTemplate } from "../../../model/enums";
import { getTileDimensions } from "../../../viewModel/utility/CarouselTemplateUtil";
import type { CarouselSkeletonProps } from "./CarouselSkeletonProps";

import "./CarouselSkeleton.scss";

/** Tile gap in vw units — must stay in sync with Carousel.scss $carousel-tile-gap. */
const TILE_GAP_VW: number = 1.563;

/** Base viewport width in pixels used for tile calculations. */
const BASE_VIEWPORT_WIDTH_PX: number = 1920;

/**
 * Placeholder shown while the initial Screens API fetch is in flight.
 * Renders skeleton tiles that match the size and layout of the specified carousel template.
 * The calling screen decides which template to render by passing it as a prop.
 * Supports all CarouselTemplate variants — tile dimensions and tiles-per-row are
 * derived automatically from the template so callers need not compute them.
 */
const CarouselSkeleton: FunctionComponent<CarouselSkeletonProps> = ({
  count = 3,
  template = CarouselTemplate.LANDSCAPE_HORIZONTAL
}): JSX.Element => {
  const tileDimensions = getTileDimensions(template);

  /**
   * Calculate the number of skeleton tiles to render per carousel row.
   * Uses the tile width from the resolved template dimensions to determine
   * how many tiles fit within the default 1920px viewport width.
   */
  const calculateSkeletonTilesPerRow = (): number => {
    const tileWidthPx = (tileDimensions.widthVw / 100) * BASE_VIEWPORT_WIDTH_PX;
    const gapWidthPx = (TILE_GAP_VW / 100) * BASE_VIEWPORT_WIDTH_PX;
    const tileStepPx = tileWidthPx + gapWidthPx;
    const tilesPerRow: number = Math.floor(
      (BASE_VIEWPORT_WIDTH_PX + gapWidthPx) / tileStepPx
    );
    return Math.max(1, tilesPerRow);
  };

  const skeletonTilesPerRow = calculateSkeletonTilesPerRow();
  const templateClass = `tile--${template}`;

  /**
   * Inline styles for skeleton tiles to match the template's dimensions.
   * Both width and height are expressed in vw units so tiles exactly match
   * the live tiles regardless of the surrounding container width.
   */
  const skeletonTileStyle: CSSProperties = {
    width: `${tileDimensions.widthVw}vw`,
    height: `${tileDimensions.aspectRatio * tileDimensions.widthVw}vw`,
    flexShrink: 0
  };

  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="carousel carousel-skeleton" aria-hidden="true">
          {/* Header placeholder — sits where carousel__title renders */}
          <div className="carousel-skeleton__header" />
          {/* Tile strip — inherits carousel__container / viewport / track layout */}
          <div className="carousel__container">
            <div className="carousel__viewport">
              <div className="carousel__track">
                {Array.from({ length: skeletonTilesPerRow }, (__, j) => (
                  <div
                    key={j}
                    style={skeletonTileStyle}
                    className={`carousel-skeleton__tile ${templateClass}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default CarouselSkeleton;
